import type { components } from "@/api-schema";
import { ServiceHistoryStatus, VehicleDrivetrain, VehicleUsage } from "@/api-schema-enums";
import { Can } from "@/casl-provider";
import { dateToInputString, parseApiDateOnly } from "@/common/helpers/date-input";
import { formatCurrency } from "@/common/helpers/format-currency";
import { useNumberFormatLocale } from "@/common/helpers/number-format";
import { getVehicleStatusLabel } from "@/common/helpers/vehicle-status";
import {
  VEHICLE_COLOR_LABELS,
  VEHICLE_FUEL_TYPE_LABELS,
  VEHICLE_KIND_LABELS,
  VEHICLE_PAINT_TYPE_LABELS,
  VEHICLE_TYPE_LABELS,
} from "@/common/helpers/vehicle-type-labels";
import { Badge, BadgeColorVariant } from "@/components/ui/badge";
import { CommandButton } from "@/components/command-palette/command-button";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { RelationLink } from "@/components/relation-link/relation-link";
import type { RelationSubject } from "@/components/relation-link/relation-path";
import {
  getVehicleAccidentStateLabel,
  getVehicleBooleanLabel,
  getVehicleValuationRimTypeLabel,
} from "@/modules/fab/vehicle-valuation/_helpers/vehicle-valuation-metadata";
import { RelocationOrderDetailInfoRow } from "@/views/relocation-order-detail/_components/relocation-order-detail-info-row";
import { CarIcon, ChevronDownIcon, ChevronUpIcon, EyeIcon } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

type Vehicle = components["schemas"]["Vehicle"];

interface VehicleInfoCardProps {
  headerActions?: ReactNode;
  vehicle: Vehicle;
  showVehicleDetailLink?: boolean;
}

type TranslateFn = (key: string) => string;

function renderRelation(
  subject: RelationSubject,
  relation: { uuid: string; name: string } | null | undefined,
): ReactNode {
  if (!relation) {
    return undefined;
  }

  return (
    <RelationLink subject={subject} uuid={relation.uuid}>
      {relation.name}
    </RelationLink>
  );
}

function formatInteger(value: number | null | undefined, locale: string): string | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return undefined;
  }

  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
}

function formatOptionalCurrency(value: number | null | undefined, locale: string): string | undefined {
  return typeof value === "number" && Number.isFinite(value) ? formatCurrency(value, { locale }) : undefined;
}

export function getAllWheelDriveDisplay(drivetrain: string | null | undefined, t: TranslateFn): string | undefined {
  if (drivetrain === VehicleDrivetrain.FourWheelDrive) {
    return t("common:Yes");
  }
  if (drivetrain === VehicleDrivetrain.FrontWheelDrive || drivetrain === VehicleDrivetrain.RearWheelDrive) {
    return t("common:No");
  }
  if (drivetrain === VehicleDrivetrain.UNKNOWN) {
    return t("masterdata:VehicleDrivetrainUnknown");
  }
  return undefined;
}

function getVehicleEnumLabel(
  value: string | null | undefined,
  labels: Record<string, { en: string; de: string }>,
  language: string,
): string | undefined {
  if (!value) {
    return undefined;
  }

  return labels[value]?.[language === "de" ? "de" : "en"] ?? value;
}

function getServiceHistoryLabel(
  serviceHistory: ServiceHistoryStatus | null | undefined,
  t: ReturnType<typeof useTranslation>["t"],
): string | undefined {
  const labels: Partial<Record<ServiceHistoryStatus, string>> = {
    [ServiceHistoryStatus.UNKNOWN]: t("masterdata:ServiceHistoryUnknown"),
    [ServiceHistoryStatus.NONE]: t("masterdata:ServiceHistoryNone"),
    [ServiceHistoryStatus.PARTIAL]: t("masterdata:ServiceHistoryPartial"),
    [ServiceHistoryStatus.FullServiceHistory]: t("masterdata:ServiceHistoryFull"),
    [ServiceHistoryStatus.FullDealerServiceHistory]: t("masterdata:ServiceHistoryFullDealer"),
  };

  return serviceHistory ? labels[serviceHistory] : undefined;
}

function getVehicleUsageLabel(
  vehicleUsage: VehicleUsage | null | undefined,
  t: ReturnType<typeof useTranslation>["t"],
): string | undefined {
  const labels: Partial<Record<VehicleUsage, string>> = {
    [VehicleUsage.RegularPersonalCommercial]: t("masterdata:VehicleUsageRegular"),
    [VehicleUsage.RENTAL]: t("masterdata:VehicleUsageRental"),
    [VehicleUsage.TaxiRideHailing]: t("masterdata:VehicleUsageTaxi"),
    [VehicleUsage.DrivingSchool]: t("masterdata:VehicleUsageDrivingSchool"),
    [VehicleUsage.DEMO]: t("masterdata:VehicleUsageDemo"),
    [VehicleUsage.FleetPool]: t("masterdata:VehicleUsageFleetPool"),
    [VehicleUsage.DeliveryCourier]: t("masterdata:VehicleUsageDelivery"),
    [VehicleUsage.SpecialPurpose]: t("masterdata:VehicleUsageSpecialPurpose"),
    [VehicleUsage.CONSTRUCTION]: t("masterdata:VehicleUsageConstruction"),
    [VehicleUsage.ManagementCompanyCar]: t("masterdata:VehicleUsageManagementCompanyCar"),
    [VehicleUsage.EmployeeCompanyCar]: t("masterdata:VehicleUsageEmployeeCompanyCar"),
    [VehicleUsage.UNKNOWN]: t("masterdata:VehicleUsageUnknown"),
  };

  return vehicleUsage ? labels[vehicleUsage] : undefined;
}

export function VehicleInfoCard({ headerActions, vehicle, showVehicleDetailLink = true }: VehicleInfoCardProps) {
  const { t, i18n } = useTranslation(["fit", "common", "masterdata", "fab"]);
  const numberFormatLocale = useNumberFormatLocale();
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const brand = vehicle.modelGeneration?.modelFamily?.brand;
  const owningBranch = vehicle.owningBranch;
  const currentBranch = vehicle.currentBranch;
  const isBlocked = !!vehicle.vehicleBlock;
  const mileage = formatInteger(vehicle.mileage, numberFormatLocale);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <CarIcon className="h-5 w-5" />
            {t("fit:VehicleInformation")}
          </CardTitle>

          {headerActions || (showVehicleDetailLink && vehicle.uuid) ? (
            <div className="flex flex-wrap items-center gap-2">
              {showVehicleDetailLink && vehicle.uuid ? (
                <Can I="read" a="Vehicle">
                  <CommandButton
                    commandId={`vehicle-details-${vehicle.uuid}`}
                    label={t("masterdata:VehicleDetails")}
                    icon={<EyeIcon className="h-4 w-4" />}
                    to={`~/masterdata/vehicle/${vehicle.uuid}`}
                    size={ButtonSize.Sm}
                    variant={ButtonVariant.Outlined}
                    colorVariant={ButtonColorVariant.Secondary}
                  />
                </Can>
              ) : null}
              {headerActions}
            </div>
          ) : null}
        </div>
      </CardHeader>

      <CardContent>
        <div className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          <RelocationOrderDetailInfoRow label={t("common:Brand")} value={renderRelation("Brand", brand)} />
          <RelocationOrderDetailInfoRow
            label={t("masterdata:ModelFamily")}
            value={vehicle.modelGeneration?.modelFamily?.name}
          />
          <RelocationOrderDetailInfoRow label={t("masterdata:ModelGeneration")} value={vehicle.modelGeneration?.name} />
          <RelocationOrderDetailInfoRow label={t("masterdata:ModelDescription")} value={vehicle.modelDescription} />
          <RelocationOrderDetailInfoRow label={t("fit:VIN")} value={vehicle.vin} mono />
          <RelocationOrderDetailInfoRow label={t("common:LicensePlate")} value={vehicle.licensePlate} mono />
          <RelocationOrderDetailInfoRow label={t("common:Status")} value={getVehicleStatusLabel(vehicle.status, t)} />
          <RelocationOrderDetailInfoRow
            label={t("masterdata:Mileage")}
            value={mileage === undefined ? undefined : `${mileage} ${t("common:km")}`}
          />
          <RelocationOrderDetailInfoRow
            label={t("common:RegistrationDate")}
            value={dateToInputString(parseApiDateOnly(vehicle.registrationDate)) || undefined}
          />
          <RelocationOrderDetailInfoRow
            label={t("common:OwningBranch")}
            value={renderRelation("Branch", owningBranch)}
          />
          <RelocationOrderDetailInfoRow
            label={t("fit:CurrentBranch")}
            value={renderRelation("Branch", currentBranch)}
          />
        </div>

        {isBlocked && (
          <div className="mt-3">
            <Badge colorVariant={BadgeColorVariant.Error}>{t("fit:VehicleBlocked")}</Badge>
          </div>
        )}

        <Collapsible open={isMoreOpen} onOpenChange={setIsMoreOpen} className="mt-3">
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              size={ButtonSize.Sm}
              variant={ButtonVariant.Ghost}
              colorVariant={ButtonColorVariant.Secondary}
            >
              {isMoreOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
              {t(isMoreOpen ? "common:ShowLess" : "common:ShowMore")}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-3">
            <div className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              <RelocationOrderDetailInfoRow
                label={t("masterdata:VehicleKind")}
                value={getVehicleEnumLabel(vehicle.vehicleKind, VEHICLE_KIND_LABELS, i18n.language)}
              />
              <RelocationOrderDetailInfoRow
                label={t("masterdata:VehicleType")}
                value={getVehicleEnumLabel(vehicle.vehicleType, VEHICLE_TYPE_LABELS, i18n.language)}
              />
              <RelocationOrderDetailInfoRow
                label={t("masterdata:FuelType")}
                value={getVehicleEnumLabel(vehicle.fuelType, VEHICLE_FUEL_TYPE_LABELS, i18n.language)}
              />
              <RelocationOrderDetailInfoRow label={t("common:ManufacturerColor")} value={vehicle.manufacturerColor} />
              <RelocationOrderDetailInfoRow
                label={t("common:Color")}
                value={getVehicleEnumLabel(vehicle.color, VEHICLE_COLOR_LABELS, i18n.language)}
              />
              <RelocationOrderDetailInfoRow
                label={t("masterdata:PaintType")}
                value={getVehicleEnumLabel(vehicle.paintType, VEHICLE_PAINT_TYPE_LABELS, i18n.language)}
              />
              <RelocationOrderDetailInfoRow
                label={t("common:EngineKilowattsWithUnit")}
                value={formatInteger(vehicle.engineKilowatts, numberFormatLocale)}
              />
              <RelocationOrderDetailInfoRow
                label={`${t("masterdata:PowerPs")} (PS)`}
                value={formatInteger(vehicle.power, numberFormatLocale)}
              />
              <RelocationOrderDetailInfoRow
                label={t("common:EngineDisplacementCcmWithUnit")}
                value={formatInteger(vehicle.engineDisplacementCcm, numberFormatLocale)}
              />
              <RelocationOrderDetailInfoRow
                label={t("common:NumberDoors")}
                value={formatInteger(vehicle.numberDoors, numberFormatLocale)}
              />
              <RelocationOrderDetailInfoRow
                label={t("common:FourWheelDrive")}
                value={getAllWheelDriveDisplay(vehicle.drivetrain, t)}
              />
              <RelocationOrderDetailInfoRow
                label={t("masterdata:NumberOfHolders")}
                value={formatInteger(vehicle.numberOfHolders, numberFormatLocale)}
              />
              <RelocationOrderDetailInfoRow label="HSN" value={vehicle.hsn} mono />
              <RelocationOrderDetailInfoRow label="TSN" value={vehicle.tsn} mono />
              <RelocationOrderDetailInfoRow
                label={t("masterdata:HuDate")}
                value={dateToInputString(parseApiDateOnly(vehicle.huDate)) || undefined}
              />
              <RelocationOrderDetailInfoRow
                label={t("masterdata:ServiceHistory")}
                value={getServiceHistoryLabel(vehicle.serviceHistory, t)}
              />
              <RelocationOrderDetailInfoRow
                label={t("masterdata:VehicleUsage")}
                value={getVehicleUsageLabel(vehicle.vehicleUsage, t)}
              />
              <RelocationOrderDetailInfoRow
                label={t("fab:RimType")}
                value={getVehicleValuationRimTypeLabel(vehicle.rimType, t)}
              />
              <RelocationOrderDetailInfoRow
                label={t("masterdata:AccidentState")}
                value={getVehicleAccidentStateLabel(vehicle.accidentState, t)}
              />
              <RelocationOrderDetailInfoRow
                label={t("fab:AccidentValue")}
                value={formatOptionalCurrency(vehicle.accidentValue, numberFormatLocale)}
              />
              <div className="sm:col-span-2">
                <RelocationOrderDetailInfoRow
                  label={t("fab:AccidentDescription")}
                  value={vehicle.accidentDescription}
                />
              </div>
              <RelocationOrderDetailInfoRow
                label={t("masterdata:VatDeductible")}
                value={getVehicleBooleanLabel(vehicle.vatDeductible, t)}
              />
              <RelocationOrderDetailInfoRow
                label={t("masterdata:Reimport")}
                value={getVehicleBooleanLabel(vehicle.reimport, t)}
              />
              {vehicle.exchangeEngine ? (
                <>
                  <RelocationOrderDetailInfoRow
                    label={t("masterdata:ExchangeDate")}
                    value={dateToInputString(parseApiDateOnly(vehicle.exchangeEngine.exchangeDate)) || undefined}
                  />
                  <RelocationOrderDetailInfoRow
                    label={t("masterdata:VehicleMileageAtExchange")}
                    value={formatInteger(vehicle.exchangeEngine.vehicleMileageAtExchange, numberFormatLocale)}
                  />
                  <RelocationOrderDetailInfoRow
                    label={t("masterdata:EngineMileageAtExchange")}
                    value={formatInteger(vehicle.exchangeEngine.engineMileageAtExchange, numberFormatLocale)}
                  />
                  <div className="sm:col-span-2">
                    <RelocationOrderDetailInfoRow
                      label={t("masterdata:ExchangeNotes")}
                      value={vehicle.exchangeEngine.notes}
                    />
                  </div>
                </>
              ) : null}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}
